import { ReactElement, useEffect, useState } from "react";
import moment from "moment";
import { Box, Container, Flex, Grid, GridItem, Heading, Text, Image, Divider, SimpleGrid, Spacer, Button, useDisclosure, Stack, HStack } from "@chakra-ui/react";
import Layout from "scenes/layouts";
import PartnerService from "services/partners/partner-service";
import { useParams } from "react-router-dom";
import LoadingScreen from "components/loading-screen";
import { FiFacebook, FiGlobe, FiInstagram, FiMapPin, FiTwitter } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { EmailIcon } from "@chakra-ui/icons";
import MemberService from "services/members/member-service";
import ChangeMemberDetail from "../change-detail";
import ChangeMemberPassword from "../change-password";
import ProgramMember from "./program-member";

const MemberProfileScene = (): ReactElement => {
    const { memberId } = useParams();
    const [member, setMember] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { isOpen: isShowChangeAccountDialog, onOpen: onShowChangeAccountDialog, onClose: onCloseChangeAccountDialog } = useDisclosure();
    const { isOpen: isShowChangePasswordDialog, onOpen: onShowChangePasswordDialog, onClose: onCloseChangePasswordDialog } = useDisclosure();

    useEffect(() => {
        setIsLoading(true);
        MemberService.GetMember(memberId).then((res: any) => {
            setMember(res.result);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [memberId]);

    if (isLoading)
        return <LoadingScreen />;

    return (
        <Layout>
            <Container maxWidth={"8xl"} mt={2} mb={5}>
                <Box>
                    <Flex>
                        <Heading fontSize={'2xl'} fontWeight={"extrabold"}>Profile {member.fullName}</Heading>
                        <Spacer />
                        <HStack>
                            <Button ml={2} variant={"outline"} onClick={onShowChangeAccountDialog}>Change Account</Button>
                            <Button ml={2} variant={"outline"} onClick={onShowChangePasswordDialog}>Change Password</Button>

                        </HStack>
                        {
                            isShowChangeAccountDialog &&
                            <ChangeMemberDetail
                                member={member}
                                onClose={onCloseChangeAccountDialog}
                            />
                        }
                        {
                            isShowChangePasswordDialog &&
                            <ChangeMemberPassword
                                member={member}
                                onClose={onCloseChangePasswordDialog}
                            />
                        }
                    </Flex>
                </Box>
                <Box mt={6}>
                    <Box rounded={'lg'} boxShadow={'md'} p={8}>
                        <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(5, 1fr)' }} gap={4}>
                            <GridItem>
                                <Box boxSize={"200px"}>
                                    <Image rounded={'lg'} src={member.profilePictureFullUrl || 'https://res.cloudinary.com/ruang-trader/image/upload/v1652634569/logosvillage/default-iser_nvxogs.webp'} />
                                </Box>
                            </GridItem>
                            <GridItem colSpan={4}>
                                <Box>
                                    <Heading fontSize={'2xl'} fontWeight={'extrabold'}>{member.fullName}</Heading>
                                    <Flex alignItems={'center'} mt={2}>
                                        <EmailIcon />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>{member.emailAddress}</Text>
                                    </Flex>
                                    <Flex alignItems={'center'} mt={2}>
                                        <FaWhatsapp />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>{member.phoneNumber}</Text>
                                    </Flex>

                                    <Flex alignItems={'center'} fontSize={'sm'} mt={2}>
                                        <FiMapPin />
                                        <Text ml={2}>{member.city ? `${member.city}, ${member.country}` : '-'}</Text>
                                    </Flex>

                                    <Text fontSize={'sm'} mt={1} fontWeight={'bold'}>{member.dateOfBirth ? moment(member.dateOfBirth).format("DD MMMM yyyy") : '-'}</Text>
                                </Box>
                            </GridItem>
                        </Grid>
                        <Divider my={3} />
                        <Box>
                            <Heading fontSize={'md'} fontWeight={'bold'}>Pekerjaan</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }}>
                                <Box py={2} flex={1}>
                                    <Text fontSize={'sm'} color={'gray.500'}>Profesi</Text>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.workProfession || '-'}</Text>
                                </Box>
                                <Box py={2} flex={1}>
                                    <Text fontSize={'sm'} color={'gray.500'}>Industri</Text>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.workIndustry || '-'}</Text>
                                </Box>
                            </SimpleGrid>
                        </Box>
                        <Divider my={3} />
                        <Box>
                            <Heading fontSize={'md'} fontWeight={'bold'}>Tentang {member.fullName}</Heading>
                            <Flex>
                                <Box py={2}>
                                    <Text fontSize={'sm'}>
                                        {member.about || '-'}
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        <Divider my={3} />
                        <Box>
                            <Heading fontSize={'md'} fontWeight={'bold'}>Sosial Media</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }}>
                                <Box py={2}>
                                    <Flex alignItems={'center'}>
                                        <FiGlobe />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>Website</Text>
                                    </Flex>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.website || '-'}</Text>
                                </Box>
                                <Box py={2}>
                                    <Flex alignItems={'center'}>
                                        <FiFacebook />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>Facebook</Text>
                                    </Flex>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.facebook || '-'}</Text>
                                </Box>
                            </SimpleGrid>
                            <SimpleGrid columns={{ base: 1, md: 2 }}>
                                <Box py={2}>
                                    <Flex alignItems={'center'}>
                                        <FiTwitter />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>Twitter</Text>
                                    </Flex>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.twitter || '-'}</Text>
                                </Box>
                                <Box py={2}>
                                    <Flex alignItems={'center'}>
                                        <FiInstagram />
                                        <Text ml={2} fontSize={'sm'} color={'gray.500'}>Instagram</Text>
                                    </Flex>
                                    <Text fontSize={'sm'} fontWeight={'bold'}>{member.instagram || '-'}</Text>
                                </Box>
                            </SimpleGrid>
                        </Box>
                    </Box>
                </Box>
                <Box mt={6}>
                    <Text fontSize={'xl'} fontWeight={"bold"}>Bergabung di program:</Text>

                    <Box rounded={'lg'} boxShadow={'md'} p={8} mt={3}>
                        {
                            member.programs.length === 0 ?
                                <>
                                    <Text fontSize={'sm'} color={'red.500'} align={"center"}>Belum bergabung di program apapun. Lakukan follow up segera.</Text>
                                </>
                                :

                                <SimpleGrid columns={[1, 2, 3]} gap={6}>
                                    {
                                        member.programs.map((program: any) => {
                                            return (
                                                <ProgramMember key={program.id} program={program} />
                                            )
                                        })
                                    }
                                </SimpleGrid>
                        }
                    </Box>
                </Box>
            </Container>
        </Layout>
    );
};

export default MemberProfileScene;